import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { take } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ContractService } from '@app/shared/services/contract.service';
import { DocumentGenerationService } from '@app/shared/services/document-generation.service';
import { authFeatureSelector } from '@app/state/app.selectors';
import { AppState } from '@app/state/app.state';
import { UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { Store } from '@ngrx/store';
import LogRocket from 'logrocket';
import { AlertComponent } from '../../alert/alert.component';
import { ConfirmComponent } from '../../confirm/confirm.component';
import { MultipleAddressDialogComponent } from '../../address/multiple-address-dialog/multiple-address-dialog/multiple-address-dialog.component';


@Component({
  selector: 'app-contract-generation',
  templateUrl: './contract-generation.component.html',
  styleUrls: ['./contract-generation.component.scss'],
})
export class ContractGenerationComponent implements OnInit, OnDestroy {
  // TODO: these dont belong in this component and should be removed
  @Input() schemaId: number;
  @Input() isNewContract: boolean;
  @Input() disabled: boolean;
  @Input() multipleAddressDisabled: boolean;
  @Input() quoteRequestId: number;
  @Input() contractData: any;
  @Input() customerType: string;
  @Output() loading: boolean;
  @Output() contractGenerated: EventEmitter<any> = new EventEmitter();

  private user: UtilityHubIdentity;
  private mutationObserver: MutationObserver;
  private multipleAddressBtn: HTMLElement;
  private multipleAddressBtnClickHandler: () => void;
  public isDefaultAddressChkboxFound: boolean = false;


  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private contractService: ContractService,
    private documentGenerationService: DocumentGenerationService,
  ) {
    this.store.select(authFeatureSelector).pipe(take(1)).subscribe((authState) => {
      this.user = authState.utilityHubIdentity;
    });
  }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    if (this.multipleAddressBtn && this.multipleAddressBtnClickHandler) {
      this.multipleAddressBtn.removeEventListener('click', this.multipleAddressBtnClickHandler);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['multipleAddressDisabled']) {
      const button = document.querySelector('.contract-button button');
      this.modifyMultipleAddressBtnStyle(this.multipleAddressDisabled, button);
    }
  }

  ngAfterViewInit() {

    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (!this.isDefaultAddressChkboxFound) {
          const useDefaultAddressCheckbox = document.getElementById('#/properties/useDefaultAddress');
          if (useDefaultAddressCheckbox) {
            this.mutationObserver.disconnect(); // Stop observing once the element is found
            this.isDefaultAddressChkboxFound = true;
            this.createMultipleAddressesBtn(useDefaultAddressCheckbox);
          }
        }
      });
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  async contractConfirmationPopup(title: string, message: string) {
    const dialog = this.dialog.open(ConfirmComponent, {
      width: '30%',
      data: {
        title,
        message,
        primaryButtonText: 'Send Contract',
      },
      disableClose: true
    });

    return dialog.afterClosed().toPromise();
  }

  contractIsPdf() {
    return this.contractData.contractType !== 'DocuSign';
  }

  alertResponsePopUp(title: string, message: string): MatDialogRef<AlertComponent> {
    return this.dialog.open(AlertComponent, {
      width: '30%',
      data: {
        title,
        message,
      },
      disableClose: true
    });
  }

  async generateDocusignContract() {
    const customerEmail = this.contractData.contact.email;
    // const contractConfirmAccepted = await this.contractConfirmationPopup(
    //   'Send Docusign Contract',
    //   `Send docusign contract to ${customerEmail}`
    // );

    // if (!contractConfirmAccepted) return;

    try {
      this.loading = true;

      await this.save(this.contractData, this.schemaId, this.quoteRequestId);

      await this.documentGenerationService
        .generateContractDocument(this.quoteRequestId, this.user?.id, customerEmail, this.user?.currentOrganisation?.name, 'DocuSign')
        .toPromise();

      LogRocket.track('contract_generated');
      this.loading = false;

      // await this.alertResponsePopUp('Success Docusign Sent', `Docusign contract successfully sent to ${customerEmail} for signature.`)
      //   .afterClosed()
      //   .toPromise();

      this.contractGenerated.emit(true);
    } catch {
      this.alertResponsePopUp('Error', `Failed to send docusign contract to ${customerEmail}.`);
      this.loading = false;
    }
  }

  async generatePdfContract(emailPdf: boolean, downloadDocument: boolean, preview: boolean) {
    const customerEmail = this.contractData.contact.email;

    if (emailPdf) {
      const contractConfirmAccepted = await this.contractConfirmationPopup('Send PDF Contract', `Send PDF contract to ${customerEmail}`);
      if (!contractConfirmAccepted) return;
    }

    try {
      this.loading = true;

      await this.save(this.contractData, this.schemaId, this.quoteRequestId);

      const docGenRes = await this.documentGenerationService
        .generateContractDocument(
          this.quoteRequestId,
          this.user?.id,
          customerEmail,
          this.user?.currentOrganisation?.name,
          'Pdf',
          downloadDocument,
          emailPdf,
          preview
        )
        .toPromise();

      if (!preview) {
        this.contractGenerated.emit(true);
      }
      LogRocket.track('contract_generated');

      if (downloadDocument) {
        this.downloadPDF(docGenRes);
      }

      this.loading = false;

      if (preview)
      {
        this.generateDocusignContract();
      }

      if (emailPdf) {
        this.alertResponsePopUp('Success PDF Sent', `PDF contract successfully sent to ${customerEmail} for signature.`);
      }
    } catch {
      this.alertResponsePopUp('Error', `Failed to generate PDF contract.`);
      this.loading = false;
    }
  }

  async openMultipleAddressPopup() {

    this.dialog.open(MultipleAddressDialogComponent, {
      disableClose: true,
      width: '80%',
      height: '50%',
      data: {
        title: `Multiple Addresses`,
        quoteRequestId: this.quoteRequestId,
        contractData: this.contractData,
        customerType: this.customerType,
      },
    });
  }

  // TODO Remove the save functionality from here and handle this logic only in parent component
  async save(contractData: string, schemaId: number, quoteRequestId: number): Promise<any> {

    contractData = this.checkForSameBillingAddressCheckbox(contractData);

    if (this.isNewContract) await this.contractService.createContract(quoteRequestId, contractData, schemaId);
    else await this.contractService.updateContract(quoteRequestId, contractData, schemaId);

    this.isNewContract = false;
    return true;
  }

  private downloadPDF(data) {
    const linkSource = window.URL.createObjectURL(data);
    const downloadLink = document.createElement('a');
    const fileName = 'contract.zip';

    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  }

  private checkForSameBillingAddressCheckbox(contractData: string) {
    var contractDataParsed = JSON.parse(JSON.stringify(contractData));
    if (contractDataParsed.billingAddressSameAsCompanyAddress == null || typeof(contractDataParsed.billingAddressSameAsCompanyAddress) == undefined) {
      contractDataParsed.billingAddressSameAsCompanyAddress = false;
      contractData = contractDataParsed;
    }
    return contractDataParsed;
  }

  private createMultipleAddressesBtn(useDefaultAddressCheckbox: Element) {

    const outerDiv = document.createElement('div');
    outerDiv.setAttribute('style', 'margin-top: 10px;');

    const span = document.createElement('span');
    span.classList.add('contract-button');

    const btnDisabledClass = this.multipleAddressDisabled ? 'mat-button-disabled' : '';
    const button = document.createElement('button');

    if (btnDisabledClass) button.classList.add(btnDisabledClass);

    button.classList.add('mat-focus-indicator', 'mat-flat-button', 'mat-button-base', 'mat-accent');
    button.setAttribute('ng-reflect-disabled', 'true');
    button.setAttribute('ng-reflect-color', 'accent');
    button.setAttribute('color', 'accent');

    this.modifyMultipleAddressBtnStyle(this.multipleAddressDisabled, button);

    const buttonWrapper = document.createElement('span');
    buttonWrapper.classList.add('mat-button-wrapper');
    buttonWrapper.textContent = ' Multiple Addresses ';

    this.multipleAddressBtnClickHandler = () => this.openMultipleAddressPopup();
    this.multipleAddressBtn = button;
    button.addEventListener('click', this.multipleAddressBtnClickHandler);

    button.appendChild(buttonWrapper);
    span.appendChild(button);
    outerDiv.appendChild(span);

    if (useDefaultAddressCheckbox) {
      useDefaultAddressCheckbox.parentNode.insertBefore(outerDiv, useDefaultAddressCheckbox.nextSibling);
    }
  }

  private modifyMultipleAddressBtnStyle(isMultipleAddressDisabled: boolean, button: Element) {
    if (button) {
      if (isMultipleAddressDisabled) {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('ng-reflect-disabled', 'true');
        button.classList.add('mat-button-disabled');
      } else {
        button.removeAttribute('disabled');
        button.setAttribute('ng-reflect-disabled', 'false');
        button.classList.remove('mat-button-disabled');
      }
    }
  }

}
